import { useEffect, useState } from "react";
import navigate from "../utilities/navigation";
import { Path } from "../utilities/constants";
import actions from "../actions/actions";
import { GuiTextbox } from "../components/global-ui/gui-textbox";
import { ToastContainer } from "../components/toast";
import Spinner from "../components/loading-spinner";
import { useUpdateState } from "../utilities/utilities";
import "../styles/setup-password.scss";
import { showToast } from "../components/toast";

const SetupPassword = () => {
    const [state, update] = useUpdateState({
        password: "",
        confirmPassword: "",
        error: "",
        isPasswordValid: false,
        isPasswordMatch: false,
        isLoading: false,
    });

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const { password, confirmPassword, error, isPasswordValid, isPasswordMatch, isLoading } = state;

    const token = new URLSearchParams(window.location.search).get("token");

    useEffect(() => {
        if (!token) {
            navigate(Path.Login);
        }
        document.body.style.backgroundColor = "#b8c9df";
        return () => {
            document.body.style.backgroundColor = "";
        };
    }, [token]);

    const validatePassword = (password) => {
        const regex =
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@#$%^&*!])[A-Za-z\d@#$%^&*!]{15,}$/;
        return regex.test(password);
    };

    const updateValue = (name, value) => {
        const isValid = name === "password" ? validatePassword(value) : isPasswordValid;
        const isMatch = name === "password" ? value === confirmPassword : password === value;
        update({
            [name]: value,
            isPasswordValid: name === "password" ? isValid : isPasswordValid,
            isPasswordMatch: name === "confirmPassword" ? value === password : isPasswordMatch,
            error: null,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isPasswordValid) {
            update({ error: "Please ensure the password meets all requirements." });
            return;
        }

        if (!isPasswordMatch) {
            update({ error: "Passwords do not match. Please try again." });
            return;
        }

        update({ isLoading: true });

        try {
            const res = await actions.user.setupPassword(token, password);
            if (res.success) {
                showToast("success", res.message || "Password created successfully!");
                navigate(Path.Messages);
                setTimeout(() => {
                    window.location.reload();
                }, 200);
            } else {
                showToast("error", res.message);
                update({ error: res.message || "Error setting up the password.", isLoading: false });
            }
        } catch (err) {
            update({ error: "Unexpected error occurred.", isLoading: false });
            console.error(err);
        }
    };

    const isButtonDisabled = !isPasswordValid || !isPasswordMatch || !password || !confirmPassword || isLoading;

    return (
        <div className="setup-password-wrapper">
            <div className="setup-password-container">
                {/* Logo */}
                <div className="setup-password-logo">
                    <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M40 0C17.9086 0 0 17.9086 0 40C0 62.0914 17.9086 80 40 80C62.0914 80 80 62.0914 80 40C80 17.9086 62.0914 0 40 0Z" fill="#E8EEF7"/>
                        <path d="M40 10C23.4315 10 10 23.4315 10 40C10 56.5685 23.4315 70 40 70C56.5685 70 70 56.5685 70 40C70 23.4315 56.5685 10 40 10Z" fill="#1968D7"/>
                        <path d="M45 35L35 50L30 45" stroke="white" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </div>

                <div className="setup-password-header">
                    <h1 className="setup-password-title">Create New Password</h1>
                    <p className="setup-password-subtitle">
                        Your new password must be different<br />from your previous one.
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="setup-password-form">
                    {/* Error Alert */}
                    {error && (
                        <div className="alert alert-error">
                            <i className="fa fa-exclamation-circle"></i>
                            <span>{error}</span>
                        </div>
                    )}

                    {/* Password Field */}
                    <div className="password-input-wrapper">
                        <GuiTextbox
                            label="Password"
                            name="password"
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={updateValue}
                        />
                        <button
                            type="button"
                            className="visibility-toggle"
                            onClick={() => setShowPassword(prev => !prev)}
                            title={showPassword ? "Hide password" : "Show password"}
                        >
                            <i className={`fa ${showPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
                        </button>
                    </div>

                    {/* Password Requirements */}
                    <div className="password-requirements-simple">
                        <ul>
                            <li className={password.length >= 15 ? "met" : ""}>15+ characters</li>
                            <li className={/[A-Z]/.test(password) ? "met" : ""}>1 uppercase</li>
                            <li className={/[a-z]/.test(password) ? "met" : ""}>1 lowercase</li>
                            <li className={/[0-9]/.test(password) ? "met" : ""}>1 number</li>
                            <li className={/[@#$%^&*!]/.test(password) ? "met" : ""}>1 special character (@#$%^&*!)</li>
                        </ul>
                    </div>

                    {/* Confirm Password Field */}
                    <div className="password-input-wrapper">
                        <GuiTextbox
                            label="Confirm Password"
                            name="confirmPassword"
                            type={showConfirmPassword ? "text" : "password"}
                            value={confirmPassword}
                            onChange={updateValue}
                        />
                        <button
                            type="button"
                            className="visibility-toggle"
                            onClick={() => setShowConfirmPassword(prev => !prev)}
                            title={showConfirmPassword ? "Hide password" : "Show password"}
                        >
                            <i className={`fa ${showConfirmPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
                        </button>
                    </div>

                    {/* Match Status */}
                    {confirmPassword && (
                        <div className={`password-match-status ${isPasswordMatch ? "matched" : "not-matched"}`}>
                            <span>{isPasswordMatch ? "✓ Password matches" : "Passwords do not match"}</span>
                        </div>
                    )}

                    {/* Submit Button */}
                    <button
                        type="submit"
                        className="setup-password-btn"
                        disabled={isButtonDisabled}
                    >
                        {isLoading && <i className="fa fa-spinner"></i>}
                        Create New Password
                    </button>
                </form>
            </div>
            <ToastContainer />
        </div>
    );
};

export default SetupPassword;
